import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import axios from 'axios';
import './styles';
import MainComponent from './components/mainComponent';
import CaptchaComponent from './components/captcha/captchaComponent';
import { NoInteractionView, alert, AlertType } from '@quyva/shared-ui';
import VendorWrapper from './components/vendorWrapper';
import NotFoundComponent from './components/NotFoundComponent';
import { useCallback } from 'react';
import {
    supportedFonts,
    AuthStatus,
    RouterPath,
    base64ToBlob,
} from '@quyva/common';
import { getUrl } from './utils';
import io from 'socket.io-client';
import { Config } from './config/envConfig';

const BREAKPOINT_WIDTH = 1024;

const defaultTheme = 'default';
const defaultFont = 'segoe';

const SOCKET_SERVER_URL = getUrl(
    `http://${Config.SOCKET_MANAGER_HOST}:${Config.SOCKET_MANAGER_PORT}`
);

function App() {
    const [name, setName] = useState('');
    const [vendorDetails, setVendorDetails] = useState(null);
    const [authState, setAuthState] = useState(AuthStatus.NOT_AUTHENTICATED);
    const [selectedProductCategory, setSelectedProductCategory] =
        useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [
        productCategoriesAndProductsList,
        setProductCategoriesAndProductsList,
    ] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Responsiveness related
    const [isSmallScreen, setIsSmallScreen] = useState(
        window.innerWidth <= BREAKPOINT_WIDTH
    );
    const [enableAudioFeature, setEnableAudioFeature] = useState(false);
    const [hasProductCategories, setHasProductCategories] = useState(false);
    const [hasProducts, setHasProducts] = useState(false);
    const [theme, setTheme] = useState(defaultTheme);
    const [font, setFont] = useState(defaultFont); // Default font

    const [socket, setSocket] = useState(null);

    useEffect(() => {
        if (vendorDetails) {
            if (vendorDetails.theme && theme !== vendorDetails.theme) {
                setTheme(vendorDetails.theme);
            }

            if (vendorDetails.font && font !== vendorDetails.font) {
                setFont(vendorDetails.font);
            }

            const heading = (
                vendorDetails.webHeading ||
                vendorDetails.name ||
                ''
            )
                .toString()
                .trim();
            document.title = heading || '';
        }
    }, [vendorDetails]);

    useEffect(() => {
        document.documentElement.className = `theme-${theme} ${supportedFonts[font].class}`;
    }, [theme, font]);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= BREAKPOINT_WIDTH);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (productCategoriesAndProductsList.length >= 1) {
            setHasProductCategories(true);
            setHasProducts(true);
        }
    }, [productCategoriesAndProductsList]);

    useEffect(() => {
        const newSocket = io(SOCKET_SERVER_URL);
        setSocket(newSocket);

        return () => {
            try {
                newSocket.disconnect();
            } catch (e) {
                return;
            }
        };
    }, []);

    const verifyAuthentication = useCallback(
        (vendorId) => {
            if (!vendorId) {
                if (authState !== AuthStatus.NOT_AUTHENTICATED) {
                    setAuthState(AuthStatus.NOT_AUTHENTICATED);
                }
                if (vendorDetails) {
                    setVendorDetails(null);
                }
                return false;
            }

            // Get productSupport data from localStorage
            const productSupport = JSON.parse(
                localStorage.getItem('productSupport')
            );

            if (!productSupport) {
                if (authState !== AuthStatus.NOT_AUTHENTICATED) {
                    setAuthState(AuthStatus.NOT_AUTHENTICATED);
                }
                return false;
            }

            const foundVendor = productSupport[vendorId];
            if (!foundVendor) {
                if (vendorDetails) {
                    productSupport[vendorId] = {
                        ...vendorDetails,
                        userName: name,
                    };

                    localStorage.setItem(
                        'productSupport',
                        JSON.stringify(productSupport)
                    );
                }
                if (authState !== AuthStatus.NOT_AUTHENTICATED) {
                    setAuthState(AuthStatus.NOT_AUTHENTICATED);
                }
                return false;
            }

            const jwtToken = foundVendor.token;
            if (!jwtToken) {
                if (authState !== AuthStatus.NOT_AUTHENTICATED) {
                    setAuthState(AuthStatus.NOT_AUTHENTICATED);
                }
                return false;
            }

            if (!vendorDetails) {
                const details = { ...foundVendor };
                delete details.token;
                setVendorDetails(details);
            }

            if (authState !== AuthStatus.AUTHENTICATED) {
                setAuthState(AuthStatus.AUTHENTICATED);
            }

            return true;
        },
        [authState, name, vendorDetails]
    );

    const getDisplayTitle = (resolved, org) => {
        return resolved.web_heading || org.name || '';
    };

    const fetchVendorDetails = useCallback(async (vendorId, navigate, path) => {
        try {
            setIsLoading(true);

            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/resolve-web-access/${encodeURIComponent(
                    vendorId
                )}`
            );

            const response = await axios.get(dynamicUrl);
            const resolved = response.data;

            if (!resolved || resolved.is_enabled === false) {
                setIsLoading(false);
                return false;
            }

            const org = resolved?.org_branding || {};
            const effectiveLogoB64 = resolved?.company_logo;
            const displayTitle = getDisplayTitle(resolved, org);
            const modifiedVendorDetails = {
                id: vendorId,
                name: displayTitle,
                logo: effectiveLogoB64
                    ? base64ToBlob(effectiveLogoB64, 'image/png')
                    : null,
                domain: org.website || '',
                copyRightText: org.copy_right_text || '',
                theme: resolved.theme,
                font: resolved.font,
                allowAudioFeature: resolved.allow_audio_feature,
                repoId: resolved.repo_id,
                virtualRepoId: resolved.virtual_repo_id,
                webHeading: resolved.web_heading,
                repoName: resolved.repo_name,
                repoType: resolved.repo_type,
                isGlobalRepo: !!resolved.is_global_repo,
                botName: resolved.bot_name || '',
                greetingMessage: resolved.greeting_message ?? null,
            };

            setVendorDetails(modifiedVendorDetails);

            // Saving data to local storage
            let productSupportUserData = JSON.parse(
                localStorage.getItem('productSupport')
            );

            if (!productSupportUserData) {
                productSupportUserData = {};
            }

            if (productSupportUserData[vendorId]) {
                productSupportUserData[vendorId] = {
                    ...productSupportUserData[vendorId],
                    ...modifiedVendorDetails,
                };
            } else {
                productSupportUserData[vendorId] = {
                    ...modifiedVendorDetails,
                };
            }

            localStorage.setItem(
                'productSupport',
                JSON.stringify(productSupportUserData)
            );

            setIsLoading(false);
            return true;
        } catch (error) {
            alert(AlertType, 'Error fetching vendor details');
            setIsLoading(false);
            return false;
        }
    }, []);

    if (isLoading) {
        return <NoInteractionView viewText={"Loading Vendor's Details"} />;
    }
    return (
        <div className={`${supportedFonts[font].class}`}>
            <BrowserRouter>
                <Routes>
                    {authState === AuthStatus.AUTHENTICATED ? (
                        <>
                            {/* Non-Authenticated routes */}
                            <Route
                                path={`/${RouterPath.CHAT_SUPPORT}/:vendorId`}
                                element={
                                    <MainComponent
                                        vendorDetails={vendorDetails}
                                        selectedProductCategory={
                                            selectedProductCategory
                                        }
                                        selectedProduct={selectedProduct}
                                        setSelectedProductCategory={
                                            setSelectedProductCategory
                                        }
                                        setSelectedProduct={setSelectedProduct}
                                        productCategoriesAndProductsList={
                                            productCategoriesAndProductsList
                                        }
                                        setProductCategoriesAndProductsList={
                                            setProductCategoriesAndProductsList
                                        }
                                        verifyAuthentication={
                                            verifyAuthentication
                                        }
                                        fetchVendorDetails={fetchVendorDetails}
                                        isSmallScreen={isSmallScreen}
                                        setAuthState={setAuthState}
                                        enableAudioFeature={enableAudioFeature}
                                        setEnableAudioFeature={
                                            setEnableAudioFeature
                                        }
                                        hasProductCategories={
                                            hasProductCategories
                                        }
                                        hasProducts={hasProducts}
                                        socket={socket}
                                    />
                                }
                            />
                            <Route
                                path={`/${RouterPath.VERIFY_CAPTCHA}/:vendorId`}
                                element={
                                    <VendorWrapper
                                        authState={authState}
                                        vendorDetails={vendorDetails}
                                        verifyAuthentication={
                                            verifyAuthentication
                                        }
                                        fetchVendorDetails={fetchVendorDetails}
                                        enableAudioFeature={enableAudioFeature}
                                        setEnableAudioFeature={
                                            setEnableAudioFeature
                                        }
                                    />
                                }
                            />

                            <Route
                                path={`/${RouterPath.VENDOR}/:vendorId`}
                                element={
                                    <VendorWrapper
                                        authState={authState}
                                        vendorDetails={vendorDetails}
                                        verifyAuthentication={
                                            verifyAuthentication
                                        }
                                        fetchVendorDetails={fetchVendorDetails}
                                        enableAudioFeature={enableAudioFeature}
                                        setEnableAudioFeature={
                                            setEnableAudioFeature
                                        }
                                    />
                                }
                            />

                            <Route
                                path={`/${RouterPath.NOT_FOUND}`}
                                element={<NotFoundComponent />}
                            />

                            <Route
                                path='*'
                                element={
                                    <Navigate to={`/${RouterPath.NOT_FOUND}`} />
                                }
                            />
                        </>
                    ) : (
                        <>
                            {/* Non-Authenticated routes */}

                            <Route
                                path={`/${RouterPath.VERIFY_CAPTCHA}/:vendorId`}
                                element={
                                    <CaptchaComponent
                                        name={name}
                                        setName={setName}
                                        setAuthState={setAuthState}
                                        vendorDetails={vendorDetails}
                                        verifyAuthentication={
                                            verifyAuthentication
                                        }
                                        fetchVendorDetails={fetchVendorDetails}
                                        isSmallScreen={isSmallScreen}
                                    />
                                }
                            />

                            <Route
                                path={`/${RouterPath.CHAT_SUPPORT}/:vendorId`}
                                element={
                                    <VendorWrapper
                                        authState={authState}
                                        vendorDetails={vendorDetails}
                                        verifyAuthentication={
                                            verifyAuthentication
                                        }
                                        fetchVendorDetails={fetchVendorDetails}
                                        enableAudioFeature={enableAudioFeature}
                                        setEnableAudioFeature={
                                            setEnableAudioFeature
                                        }
                                    />
                                }
                            />

                            <Route
                                path={`/${RouterPath.VENDOR}/:vendorId`}
                                element={
                                    <VendorWrapper
                                        authState={authState}
                                        vendorDetails={vendorDetails}
                                        verifyAuthentication={
                                            verifyAuthentication
                                        }
                                        fetchVendorDetails={fetchVendorDetails}
                                        enableAudioFeature={enableAudioFeature}
                                        setEnableAudioFeature={
                                            setEnableAudioFeature
                                        }
                                    />
                                }
                            />

                            <Route
                                path={`/${RouterPath.NOT_FOUND}`}
                                element={<NotFoundComponent />}
                            />

                            <Route
                                path='*'
                                element={
                                    <Navigate to={`/${RouterPath.NOT_FOUND}`} />
                                }
                            />
                        </>
                    )}
                </Routes>
            </BrowserRouter>
        </div>
    );
}

export default App;
